import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAddressDetail, getNetworkConfig } from "@/lib/api";
import { PoolOverview } from "@/components/explorer/PoolOverview";
import { LiquidityChart } from "@/components/explorer/LiquidityChart";
import { ADDRESS } from "@/constants/testIds";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Copy, Wallet } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AddressHistory } from "@/components/explorer/AddressHistory";
import { TokenOverview } from "@/components/explorer/TokenOverview";
import { TokenTransfers } from "@/components/explorer/TokenTransfers";
import { ContractSource, useContractSource } from "@/components/explorer/ContractSource";
import { FADE_UP } from "@/lib/motion";

const VerifiedBadge = ({ address }) => {
  const { data } = useContractSource(address);
  return data ? <Badge data-testid="address-verified-badge" className="bg-green-500/10 text-green-400 border border-green-500/30">Source Verified</Badge> : null;
};

function formatFMI(weiStr) {
  return (Number(weiStr) / 1e18).toFixed(4);
}

export default function AddressPage() {
  const { address } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["address", address],
    queryFn: () => getAddressDetail(address),
  });
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const isPool = Boolean(config?.swap?.address) && config.swap.address.toLowerCase() === address.toLowerCase();

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-gold-pulse text-center text-[#D4AF37] py-20 font-display">Loading address...</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="address-not-found" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center text-zinc-400">
        Invalid or unknown address
      </div>
    );
  }

  const isDemo = data?.isDemo;

  const copyAddr = () => {
    navigator.clipboard.writeText(address);
    toast.success("Address copied");
  };

  return (
    <div data-testid={ADDRESS.container} className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <motion.div {...FADE_UP}>
        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Address</h1>
          {isDemo && <Badge className="demo-badge">Demo</Badge>}
          {data.isContract && (
            <Badge data-testid="address-contract-badge" className="bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
              {data.token ? `Token Contract · ${data.token.symbol}` : "Contract"}
            </Badge>
          )}
          {data.isContract && <VerifiedBadge address={address} />}
        </div>

        {/* Address Card */}
        <Card className="border-[#2A2411] bg-[#0A0A0A]">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <Wallet size={18} className="text-[#D4AF37]" />
              </div>
              <div className="min-w-0 flex-1">
                <p data-testid={ADDRESS.address} className="font-mono text-sm text-white break-all">{address}</p>
                <button onClick={copyAddr} className="text-xs text-zinc-500 hover:text-[#D4AF37] flex items-center gap-1 mt-1 transition-colors">
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
            <Separator className="bg-[#2A2411]" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">FMI Balance</p>
                <p data-testid={ADDRESS.balance} className="font-display text-xl font-bold text-white">
                  {data ? formatFMI(data.balance) : "0.0000"} <span className="text-[#D4AF37]">FMI</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{data.isContract ? "Nonce" : "Transactions Sent"}</p>
                <p data-testid={ADDRESS.txCount} className="font-display text-xl font-bold text-white">
                  {data?.transactionCount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {data.token && <TokenOverview address={address} />}
        {isPool && <LiquidityChart />}
        {isPool && <PoolOverview />}
        {data.token && <TokenTransfers address={address} />}
        {data.isContract && <ContractSource address={address} />}
        <AddressHistory address={address} />
      </motion.div>
    </div>
  );
}
